import React, { useEffect, useRef, useState } from 'react';
import { formatShortDateTime } from '../../shared/time';
import {
  Dialog,
  Fact,
  IconButton,
  IconCheck,
  IconCopy,
  Row,
  SectionTitle,
  StatusLabel,
} from '../../ui/v2';
import {
  detailFulfillmentType,
  detailStatusLabel,
  formatCurrencyFromCents,
  formatDeliveryAddress,
  FULFILLMENT_LABEL,
  type Order,
  orderStatusTone,
} from './orderModel';

const PaymentIntent: React.FC<{ value: string | null }> = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number>();

  useEffect(() => {
    setCopied(false);
    return () => window.clearTimeout(timer.current);
  }, [value]);

  if (!value) return <>Not available</>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="v2-num truncate text-v2-13">{value}</span>
      <IconButton size="sm" label="Copy payment intent" onClick={copy}>
        {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
      </IconButton>
      <span aria-live="polite" className="sr-only">
        {copied ? 'Copied' : ''}
      </span>
    </span>
  );
};

const OrderDetailDialog: React.FC<{
  order: Order | null;
  open: boolean;
  onClose: () => void;
}> = ({ order, open, onClose }) => {
  const fulfillment = detailFulfillmentType(order);
  const items = order?.order_items ?? [];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={order?.customer_name ?? 'Unknown Customer'}
      description={`${order?.id ? `Order #${order.id}` : 'Order'} · ${
        order?.created_at
          ? formatShortDateTime(order.created_at)
          : 'Unknown time'
      }`}
    >
      {order && (
        <>
          <StatusLabel tone={orderStatusTone(order.status)}>
            {detailStatusLabel(order)}
          </StatusLabel>

          <dl className="mt-3 grid grid-cols-1 gap-3 border-y border-v2-line py-3 sm:grid-cols-3">
            <Fact label="Items" value={order.order_items?.length || 0} />
            <Fact label="Fulfillment" value={FULFILLMENT_LABEL[fulfillment]} />
            <Fact
              label="Payment intent"
              value={<PaymentIntent value={order.stripe_payment_intent_id} />}
              missing={!order.stripe_payment_intent_id}
            />
          </dl>

          {fulfillment === 'delivery' && (
            <dl>
              <Row label="Delivery address">{formatDeliveryAddress(order)}</Row>
            </dl>
          )}

          <section aria-label="Items" className="mt-4">
            <SectionTitle>Items</SectionTitle>
            {items.length > 0 ? (
              <ul className="mt-1">
                {items.map((item, index) => (
                  <li
                    key={index}
                    className="flex items-start justify-between gap-4 border-b border-v2-line/70 py-2 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="text-v2-13 font-medium text-v2-fg">
                        {item?.quantity || 1}{' '}
                        {item?.menu_item_name || `Item #${item?.menu_item}`}
                      </p>
                      {item?.modifier_details &&
                        item.modifier_details.length > 0 && (
                          <ul className="mt-0.5 text-v2-12 text-v2-fg-3">
                            {item.modifier_details.map((modifier, modIndex) => (
                              <li key={modIndex}>
                                {modifier.quantity ?? 1} {modifier.name}
                              </li>
                            ))}
                          </ul>
                        )}
                      {item?.note && (
                        <p className="mt-1 text-v2-12 text-v2-fg-3">
                          Note: {item.note}
                        </p>
                      )}
                    </div>
                    <span className="v2-num shrink-0 text-v2-13 font-medium text-v2-fg">
                      {formatCurrencyFromCents(
                        (item?.price || 0) * (item?.quantity || 1),
                        order.currency,
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="py-4 text-v2-12 text-v2-fg-3">
                No items in this order
              </p>
            )}
          </section>

          <section aria-label="Totals" className="mt-4">
            <SectionTitle>Totals</SectionTitle>
            <dl className="mt-1 text-v2-13">
              {order.total_price_without_taxes_and_fees !== undefined && (
                <TotalRow
                  label="Subtotal"
                  value={formatCurrencyFromCents(
                    order.total_price_without_taxes_and_fees,
                    order.currency,
                  )}
                />
              )}
              {order.delivery_fee_cents !== undefined &&
                order.delivery_fee_cents > 0 && (
                  <TotalRow
                    label="Delivery fee"
                    value={formatCurrencyFromCents(
                      order.delivery_fee_cents,
                      order.currency,
                    )}
                  />
                )}
              {order.service_fee_cents !== undefined &&
                order.service_fee_cents > 0 && (
                  <TotalRow
                    label="Service fee"
                    value={formatCurrencyFromCents(
                      order.service_fee_cents,
                      order.currency,
                    )}
                  />
                )}
              {order.tax !== undefined && order.tax > 0 && (
                <TotalRow
                  label="Tax"
                  value={formatCurrencyFromCents(order.tax, order.currency)}
                />
              )}
              <TotalRow
                strong
                label="Total"
                value={formatCurrencyFromCents(
                  order.total_price,
                  order.currency,
                )}
              />
            </dl>
          </section>
        </>
      )}
    </Dialog>
  );
};

const TotalRow: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
}> = ({ label, value, strong }) => (
  <div
    className={
      strong
        ? 'flex justify-between gap-4 border-t border-v2-line pt-2 mt-1 font-semibold text-v2-fg'
        : 'flex justify-between gap-4 py-1 text-v2-fg-2'
    }
  >
    <dt>{label}</dt>
    <dd className="v2-num text-v2-fg">{value}</dd>
  </div>
);

export default OrderDetailDialog;
